import { useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import styles from './page-header.module.scss';
import DemoBadge from '@/components/demo-badge/demo-badge';
import { findSectionByPath } from '@/config/navigation';
import { SalesAgentToggle } from '@/components/sales-agent-toggle/sales-agent-toggle';
import { useCompaniesList } from '@/hooks/use-company/use-company';
import { useIsBayonne } from '@/hooks/use-is-bayonne/use-is-bayonne';
import { useUser } from '@/hooks/use-user/use-user';
import { useTimeRangeStore } from '@/store/app.store';
import {
  inferActivePreset,
  visibleTimeRanges,
} from '@/services/dashboard-service/time-range.utils';

export function PageHeader() {
  const { t } = useTranslation(['common', 'navigation']);
  const { pathname } = useLocation();
  const isBayonne = useIsBayonne();
  const { timeRange, startTime, endTime, setTimeRangeFilter } = useTimeRangeStore();
  const { userData } = useUser();
  const { companies } = useCompaniesList();

  const section = findSectionByPath(pathname);
  // Nothing is handed over to a salesperson whose company cannot call out.
  const salesAgent = userData?.sales_agent;
  const showsAvailability =
    !!salesAgent &&
    !!companies?.find((company) => company.id === salesAgent.company_id)?.outbound_calls_enabled;

  if (!section) return null;

  const active = inferActivePreset({
    time_range: timeRange,
    start_time: startTime,
    end_time: endTime,
  });

  const showRanges = section.showRangeSwitch && !isBayonne;

  return (
    <header className={styles.header}>
      <div>
        <h1 className={styles.title}>
          {t(`navigation:sections.${section.key}.title`)}
          {section.demoBadge && <DemoBadge />}
        </h1>
        <p className={styles.subtitle}>{t(`navigation:sections.${section.key}.subtitle`)}</p>
      </div>

      <div className={styles.aside}>
        {showsAvailability && <SalesAgentToggle />}
        {/* Hidden with `visibility` rather than unmounted so the header keeps the same
            height across sections, as the mockup does. */}
        <div
          data-testid="page-header-ranges"
          className={styles.ranges}
          style={{ visibility: showRanges ? 'visible' : 'hidden' }}
        >
          {visibleTimeRanges.map((range) => (
            <button
              key={range}
              type="button"
              aria-pressed={active === range}
              className={clsx(styles.rangeButton, active === range && styles.rangeButtonActive)}
              onClick={() => setTimeRangeFilter(range)}
            >
              {t(`timeRange.${range}`)}
            </button>
          ))}
          {active === 'custom' && (
            <button type="button" disabled className={styles.rangeButtonActive}>
              {t('timeRange.custom')}
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
